import { z } from 'zod';
import { WorkoutCompletionMode, WorkoutFeedbackMood } from '../enums';

export const CreateUpdateProgressDto = z.object({
  workoutExerciseId: z.string(),
  // Progress stays keyed to workoutExerciseId (the primary slot);
  // this only records which registered alternate the client performed instead.
  performedViaAlternateId: z.string().uuid().nullable().optional(),
  progressMode: z.enum(['AUTOMATED', 'MANUAL']).nullish(),
  currentSecond: z.number().nonnegative().nullable().optional(),
  status: z.enum(['COMPLETED', 'INPROGRESS', 'IDLE']).nullable().optional(),
  feedbacks: z
    .array(
      z.object({
        configurableId: z.string(),
        feedback: z.string(),
      }),
    )
    .nullable()
    .optional(),
  scheduleId: z.string().uuid().optional(),
});

export type ApiCreateUpdateProgressDto = z.infer<
  typeof CreateUpdateProgressDto
>;

const BaseCreateWorkoutFeedbackDto = z.object({
  // 1.0-5.0, one decimal place. Also "Peak Training Effect" — same field, no
  // separate PTE field.
  effort: z
    .number()
    .min(1)
    .max(5)
    .refine((v) => Math.round(v * 10) === v * 10, {
      message: 'effort must have at most one decimal place',
    })
    .optional(),
  // Deprecated alias for `effort`, kept only so the current production
  // frontend (which still posts `rating`, 0-5, defaulting to 0 when unset)
  // keeps working until it switches to posting `effort` directly. Remove
  // once that frontend rework ships.
  rating: z.number().min(0).max(5).optional(),
  averageHeartRate: z.number().nullable().optional(),
  totalKcal: z.number().nullable().optional(),
  steps: z.number().nullable().optional(),
  distance: z.number().nullable().optional(),
  floors: z.number().nullable().optional(),
  workoutProgressId: z.string().optional(),
  // Which scheduled event was just completed. Optional for back-compat: without
  // it the endpoint could only match schedules by workoutId, and several events
  // can share one workout copy (repeated program day / copied event) — all of
  // them used to be flipped to COMPLETED by one finished session.
  scheduleId: z.string().uuid().optional(),
  // Calendar feedback is retroactive; actual player feedback keeps its finish
  // time. This selects the date rule, not the progress mode or undo behavior.
  completionSource: z.literal('MARK_AS_DONE').optional(),
  // F3.2 — stvarno trajanje sesije (est-vs-real, D-03/D-04)
  durationSec: z.number().int().positive().nullable().optional(),
  totalRestSec: z.number().int().min(0).nullable().optional(),
  maxHeartRate: z.number().int().positive().nullable().optional(),
  // The remaining two buckets of the
  // activeSec + totalRestSec + pauseSec == durationSec identity
  activeSec: z.number().int().min(0).nullable().optional(),
  pauseSec: z.number().int().min(0).nullable().optional(),
  mood: z.nativeEnum(WorkoutFeedbackMood).nullable().optional(),
  // Solo vs. with-trainer, captured alongside the rest of "Done Workout".
  completedWith: z.nativeEnum(WorkoutCompletionMode).nullable().optional(),
  // 1.12 — which trainer package a "with trainer" workout takes its session
  // from, when the client holds more than one. Peeled off before the write.
  trainerSessionUserMembershipId: z.string().uuid().nullable().optional(),
  stressPct: z.number().int().min(0).max(100).nullable().optional(),
  stressSource: z.enum(['manual', 'api']).nullable().optional(),
  // entered in hours on the FE, stored in seconds
  recoverySec: z.number().int().min(0).nullable().optional(),
  // time spent in each HR zone during the workout, entered as hh:mm:ss on
  // the FE, stored as seconds
  hrZ1Sec: z.number().int().min(0).nullable().optional(),
  hrZ2Sec: z.number().int().min(0).nullable().optional(),
  hrZ3Sec: z.number().int().min(0).nullable().optional(),
  hrZ4Sec: z.number().int().min(0).nullable().optional(),
  hrZ5Sec: z.number().int().min(0).nullable().optional(),
});

export const CreateWorkoutFeedbackDto = BaseCreateWorkoutFeedbackDto.transform(
  ({ rating, effort, ...rest }) => ({
    ...rest,
    effort: effort ?? (rating || undefined),
  }),
).refine((dto) => !dto.completionSource || !!dto.scheduleId, {
  message: 'scheduleId is required for calendar completion',
  path: ['scheduleId'],
});

export type ApiCreateWorkoutFeedbackDto = z.infer<
  typeof CreateWorkoutFeedbackDto
>;
